<template>
  <div>
    <div class="header-challenge">
      <img
        :src="cover"
        :alt="page.title"
        class="background-image"
      />
      <div class="layer-container">
        <div class="layer">
          <div class="top-content">Challenge</div>
          <h2>
            {{ page.reward }} points
            <span>à gagner</span>
          </h2>
          <h1>{{ page.title }}</h1>
          <ButtonPoint
            v-if="challengeStep === 0"
            :points="page.reward"
            class="button-point-item"
            label="Relever le challenge"
            @click="openChallenge"
          />
          <ButtonSimple
            v-if="challengeStep === 1"
            label="Challenge déjà relevé"
            class="button-point-item"
            disabled
          />
          <ButtonSimple
            v-if="challengeStep === 2"
            route="/"
            disabled
            class="button-point-item"
            label="Challenge terminé"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { challengeModal } from '~/modals/challenge'
import { loginModal } from '~/modals/login'

const props = defineProps<{
  page: {
    title?: string
    reward?: number
    image?: string
    imageMobile?: string
    isFinished?: boolean
    userStatus?: number
  }
}>()

const challengeStep = computed(() => {
  if (props.page.isFinished) return 2
  if (props.page.userStatus === 3 || props.page.userStatus === 2) return 0
  return 1
})

const { isMobile, isLargeMobile } = useCustomMediaQuery()

const cover = computed(() => {
  if (isMobile.value || isLargeMobile.value) {
    return props.page.imageMobile ?? props.page.image
  }
  return props.page.image
})

const { gtag } = useGtag()
const { authenticated } = useAuthenticated()

const openChallenge = () => {
  gtag('event', 'cta', {
    cta_name: 'Relever le challenge',
  })

  authenticated.value ? challengeModal.open() : loginModal.open()
}
</script>

<style lang="scss" scoped>
.header-challenge {
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 90vh;
  max-height: 73rem;

  .background-image {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    object-fit: cover;
    object-position: top;
  }

  .layer {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    max-width: 56.6rem;
    height: 100%;
    padding: 14rem 8rem 0;
    background: rgba($black, 0.6);

    .top-content {
      color: rgba($white, 0.47);
      font-size: 1rem;
      font-weight: 500;
      line-height: 1.3rem;
      text-transform: uppercase;
    }

    h1 {
      margin-top: 2.8rem;
      color: $white;
      font-family: SST;
      font-size: 3.2rem;
      font-weight: 400;
      line-height: 4.1rem;
      letter-spacing: 0;
    }

    h2 {
      margin-top: 0.5rem;
      padding-bottom: 3.45rem;
      color: $white;
      font-family: 'SST Condensed';
      font-size: 4.6rem;
      font-weight: bold;
      line-height: 5rem;
      letter-spacing: 0;
      border-bottom: 1px solid rgba($white, 0.47);

      span {
        display: block;
        font-size: 2.6rem;
        line-height: 3rem;
      }
    }
  }

  .subtitle {
    max-width: 37.4rem;
    margin-top: 2rem;
    color: $white;
    font-size: 1.2rem;
    line-height: 1.6rem;
  }

  .button-point-item {
    position: absolute;
    bottom: 14rem;
    left: 8rem;
  }

  &::after {
    content: '';
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 2;
    width: 0;
    height: 0;
    border-width: 0 0 5rem 100vw;
    border-style: solid;
    border-color: transparent transparent $white transparent;
  }
}

@media screen and (max-width: 700px) {
  .header-challenge {
    align-items: flex-start;
    height: auto;

    .layer {
      position: relative;
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: flex-start;
      padding: 5rem 2.5rem 8rem;

      .top-content {
        width: 100%;
      }
    }

    .button-point-item {
      position: relative;
      bottom: auto;
      left: auto;
      margin-top: 5rem;
    }
  }
}

@media screen and (min-width: 2000px) {
  .header-challenge {
    .layer-container {
      position: absolute;
      top: 0;
      left: 0;
      width: calc(50vw - 100rem + 56.6rem);
      height: 100%;
      background: rgba($black, 0.6);

      .layer {
        right: 0;
        left: auto;
        background: transparent;
      }
    }
  }
}
</style>
